/* SignatureField.jsx — iter98.99z
 *
 * Reusable e-signature capture widget. Used by:
 *   · Doctor profile "E-Signature" card (manage anytime)
 *   · Doctor onboarding "Save your signature" step (one-time prompt)
 *
 * The Rx flow (PrescriptionComplianceBlock) intentionally embeds its
 * own copy because that flow has additional tabs (Type / Profile reuse)
 * and tighter coupling with the per-case checklist. Sharing more than
 * the canvas would couple unrelated concerns.
 *
 * Saves a base64 data URL (image/png) to `value`. Empty string === no
 * signature on file. Parent owns persistence — this component is
 * pure presentational.
 */
import { useEffect, useRef, useState } from "react";
import SignatureCanvas from "react-signature-canvas";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { PencilSimpleLine, UploadSimple, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function SignatureField({ value, onChange, testid = "signature-field", height = 140 }) {
    const [mode, setMode] = useState(value ? "preview" : "draw");
    const [drawnEmpty, setDrawnEmpty] = useState(true);
    const sigRef = useRef(null);

    /* Keep mode in sync if the parent clears the value externally. */
    useEffect(() => {
        if (!value && mode === "preview") setMode("draw");
    }, [value, mode]);

    const saveDrawn = () => {
        if (!sigRef.current || sigRef.current.isEmpty()) {
            toast.error("Draw your signature first.");
            return;
        }
        try {
            const dataUrl = sigRef.current.getCanvas().toDataURL("image/png");
            onChange(dataUrl);
            setMode("preview");
            toast.success("Signature saved.");
        } catch {
            toast.error("Couldn't save signature. Try again.");
        }
    };

    const handleUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (!/^image\/(png|jpe?g)$/i.test(file.type)) {
            toast.error("Upload a PNG or JPG image.");
            return;
        }
        if (file.size > 2 * 1024 * 1024) {
            toast.error("File too large (max 2 MB).");
            return;
        }
        const reader = new FileReader();
        reader.onload = () => {
            onChange(reader.result);
            setMode("preview");
            toast.success("Signature uploaded.");
        };
        reader.readAsDataURL(file);
    };

    const clearSig = () => {
        onChange("");
        setMode("draw");
        toast.message("Signature removed.");
    };

    /* ---- preview ---- */
    if (mode === "preview" && value) {
        return (
            <div className="space-y-3" data-testid={testid}>
                {/* iter100.320 — Removed the emerald "Signature on file /
                    This will auto-populate on every prescription you sign"
                    banner. It duplicated the parent's outer E-Signature
                    label + description one row above. The image preview
                    itself communicates "on file" clearly; the action
                    buttons below make it obvious you can replace/remove. */}
                <div className="rounded-lg bg-white border border-emerald-200 p-3 flex items-center justify-center min-h-[80px]">
                    <img
                        src={value}
                        alt="Saved signature"
                        className="max-h-[100px] max-w-full object-contain"
                        data-testid={`${testid}-preview`}
                    />
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setMode("draw")}
                        className="gap-1.5 h-8 text-xs"
                        data-testid={`${testid}-replace-draw`}
                    >
                        <PencilSimpleLine size={13} weight="bold" /> Replace by drawing
                    </Button>
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setMode("upload")}
                        className="gap-1.5 h-8 text-xs"
                        data-testid={`${testid}-replace-upload`}
                    >
                        <UploadSimple size={13} weight="bold" /> Replace by uploading
                    </Button>
                    <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={clearSig}
                        className="gap-1.5 h-8 text-xs text-rose-700 hover:bg-rose-50"
                        data-testid={`${testid}-clear`}
                    >
                        <Trash size={13} weight="bold" /> Remove
                    </Button>
                </div>
            </div>
        );
    }

    /* ---- capture (draw / upload) ---- */
    return (
        <div className="space-y-3" data-testid={testid}>
            <Tabs value={mode === "preview" ? "draw" : mode} onValueChange={setMode}>
                <TabsList className="grid grid-cols-2 mb-3" data-testid={`${testid}-tabs`}>
                    <TabsTrigger value="draw" className="gap-1.5" data-testid={`${testid}-tab-draw`}>
                        <PencilSimpleLine size={13} weight="bold" /> Draw
                    </TabsTrigger>
                    <TabsTrigger value="upload" className="gap-1.5" data-testid={`${testid}-tab-upload`}>
                        <UploadSimple size={13} weight="bold" /> Upload
                    </TabsTrigger>
                </TabsList>

                <TabsContent value="draw" className="mt-0">
                    <div className="rounded-lg bg-white border border-border overflow-hidden">
                        <SignatureCanvas
                            ref={sigRef}
                            canvasProps={{
                                style: { width: "100%", height: `${height}px`, display: "block" },
                                "data-testid": `${testid}-canvas`,
                            }}
                            penColor="#0e3a52"
                            onEnd={() => setDrawnEmpty(false)}
                        />
                    </div>
                    <p className="text-[10.5px] text-muted-foreground mt-1.5">
                        Sign inside the box using your mouse, stylus, or finger.
                    </p>
                    <div className="mt-2.5 flex items-center gap-2">
                        <Button
                            type="button"
                            size="sm"
                            onClick={saveDrawn}
                            disabled={drawnEmpty}
                            className="h-8 text-xs"
                            data-testid={`${testid}-save-drawn`}
                        >
                            Save signature
                        </Button>
                        <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => { sigRef.current?.clear(); setDrawnEmpty(true); }}
                            className="h-8 text-xs"
                            data-testid={`${testid}-clear-canvas`}
                        >
                            Clear
                        </Button>
                    </div>
                </TabsContent>

                <TabsContent value="upload" className="mt-0">
                    <label
                        className="flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-border bg-secondary/30 hover:bg-secondary/50 px-4 py-8 cursor-pointer transition-colors"
                        data-testid={`${testid}-upload-zone`}
                    >
                        <UploadSimple size={22} weight="duotone" className="text-muted-foreground" />
                        <span className="text-[12.5px] font-medium text-foreground">Choose an image</span>
                        <span className="text-[10.5px] text-muted-foreground">PNG or JPG · max 2 MB · transparent PNG looks best</span>
                        <input
                            type="file"
                            accept="image/png,image/jpeg"
                            onChange={handleUpload}
                            className="sr-only"
                            data-testid={`${testid}-upload-input`}
                        />
                    </label>
                </TabsContent>
            </Tabs>
        </div>
    );
}
